import {generateUuid} from '@/utils/uuid';

export const COOKIE_CONSENT_COOKIE_NAME = 'fs-consent';
export const COOKIE_CONSENT_DATA_LAYER_EVENT = 'fs-consent-consentModeUpdate';
export const COOKIE_CONSENT_REGION_DATA_LAYER_EVENT = 'fs-consent-regionDetected';

export type CookieConsentChoices = {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
};

export type CookieConsentStoredData = {
  id: string;
  choices: CookieConsentChoices;
  timestamp: number;
};

export type GeoLookup = {
  country?: string;
  region?: string;
};

type CreateCookieConsentParams = {
  choices: CookieConsentChoices;
  id?: string;
  timestamp?: number;
};

type WriteCookieConsentParams = {
  consent: CookieConsentStoredData;
  domain?: string;
};

const COOKIE_CONSENT_MAX_AGE_SECONDS = 365 * 86400;

const EU_COUNTRY_CODES = [
  'AT',
  'BE',
  'BG',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FR',
  'GR',
  'HR',
  'HU',
  'IE',
  'IS',
  'IT',
  'LI',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'NO',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
];

export const REJECT_OPTIONAL_COOKIE_DEFAULTS = {
  essential: true,
  analytics: false,
  marketing: false,
  personalization: false,
};

const ACCEPT_ALL_COOKIE_DEFAULTS = {
  essential: true,
  analytics: true,
  marketing: true,
  personalization: true,
};

const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const isCookieConsentChoices = (value: unknown): value is CookieConsentChoices => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    value.essential === true &&
    isBoolean(value.analytics) &&
    isBoolean(value.marketing) &&
    isBoolean(value.personalization)
  );
};

const isCookieConsentStoredData = (value: unknown): value is CookieConsentStoredData => {
  if (!isRecord(value)) {
    return false;
  }

  return typeof value.id === 'string' && isCookieConsentChoices(value.choices) && typeof value.timestamp === 'number';
};

const getCookieValue = (name: string): string | undefined => {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  return cookie?.split('=').slice(1).join('=');
};

const toOptionalString = (value: unknown): string | undefined => {
  return typeof value === 'string' && value ? value : undefined;
};

const publishDetectedRegion = (region: string | undefined) => {
  window.fs_consent_pro_detected_region = region;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: COOKIE_CONSENT_REGION_DATA_LAYER_EVENT,
    region,
  });
};

export const detectGeo = (): Promise<GeoLookup | null> => {
  return fetch('https://collect.tocafootball.com/geo/', {
    method: 'POST',
    credentials: 'include',
  })
    .then((response) => {
      if (!response.ok) {
        return null;
      }

      return response.json().then((data) => {
        const geo: GeoLookup = {
          country: toOptionalString(data?.geo?.country),
          region: toOptionalString(data?.geo?.region),
        };

        publishDetectedRegion(geo.region);
        return geo;
      });
    })
    .catch(() => {
      return null;
    });
};

export const getDefaultConsentChoices = (geo: GeoLookup | null): CookieConsentChoices => {
  const country = geo?.country;
  if (!country || EU_COUNTRY_CODES.includes(country)) {
    return REJECT_OPTIONAL_COOKIE_DEFAULTS;
  }

  return ACCEPT_ALL_COOKIE_DEFAULTS;
};

export const parseCookieConsent = (rawConsent: string | undefined): CookieConsentStoredData | null => {
  if (!rawConsent) {
    return null;
  }

  try {
    const parsedConsent: unknown = JSON.parse(decodeURIComponent(rawConsent));
    if (!isCookieConsentStoredData(parsedConsent)) {
      return null;
    }

    return parsedConsent;
  } catch {
    return null;
  }
};

export const getCookieConsent = (): CookieConsentStoredData | null => {
  return parseCookieConsent(getCookieValue(COOKIE_CONSENT_COOKIE_NAME));
};

export const createCookieConsent = ({
  choices,
  id = generateUuid(),
  timestamp = Date.now(),
}: CreateCookieConsentParams): CookieConsentStoredData => ({
  id,
  choices: {
    essential: true,
    analytics: choices.analytics,
    marketing: choices.marketing,
    personalization: choices.personalization,
  },
  timestamp,
});

export const writeCookieConsent = ({
  consent,
  domain = import.meta.env.VITE_COOKIE_CONSENT_DOMAIN,
}: WriteCookieConsentParams) => {
  if (typeof document === 'undefined') {
    return;
  }

  const encodedConsent = encodeURIComponent(JSON.stringify(consent));
  const cookieDomain = domain ? `; domain=${domain}` : '';
  const secureAttribute = window.location.protocol === 'https:' ? '; Secure' : '';

  document.cookie = `${COOKIE_CONSENT_COOKIE_NAME}=${encodedConsent}; path=/${cookieDomain}; max-age=${COOKIE_CONSENT_MAX_AGE_SECONDS}; SameSite=Lax${secureAttribute}`;
};

export const pushCookieConsentDataLayerEvent = (consent: CookieConsentStoredData) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: COOKIE_CONSENT_DATA_LAYER_EVENT,
    id: consent.id,
    choices: consent.choices,
    timestamp: consent.timestamp,
  });
};
